/**
 * How far a pixel of mouse travel turns the view, in radians.
 *
 * camera-and-controls.md amendment (2026-08-25, fifth) — user request: "in the
 * game itself (in both PvP and PvE modes), we should bind camera rotation to
 * the mouse. I feel this would be the most comfortable option."
 *
 * Yaw is deliberately slower than the 0.0035 the old Alt free-look used. That
 * value was tuned for a *flick*: a brief glance over your shoulder, held for a
 * second, that then decayed back on its own. This one is how the player aims
 * for an entire run, and a sensitivity tuned for flicks overshoots constantly
 * when it is also the thing you track a circling opponent with.
 *
 * Pitch is slower still, and that asymmetry is intentional rather than sloppy:
 * the pitch range below spans 1.5 rad in total against yaw's unbounded turn, so
 * equal sensitivity would slam the view into its own clamp on any diagonal
 * sweep.
 */
const MOUSE_YAW_SENSITIVITY = 0.0025;
const MOUSE_PITCH_SENSITIVITY = 0.0018;
const SHOULDER_BLEND_TAU_S = 0.067; // ~3 time-constants ≈ 0.2s, camera-and-controls.md's stated shoulder-swap blend
/**
 * Negative = looking down (see shoulder-camera-pose.ts's sign convention:
 * positive pitch drops the eye below the anchor to look up). The old +0.35
 * default put the resting eye at chest height staring up at the character's
 * own head — found live (2026-08-21) as the actual cause of "not enough
 * visible on screen": most of the frame was consumed by the character's
 * head/back against the ceiling, with almost none of the floor ahead
 * visible. A small downward tilt raises the eye above head height and shows
 * the corridor/room ahead instead.
 */
const DEFAULT_BASE_PITCH = -0.12;
const MIN_PITCH = -0.6;
const MAX_PITCH = 0.9;

function expApproach(current: number, target: number, dtSeconds: number, tauSeconds: number): number {
  return current + (target - current) * (1 - Math.exp(-dtSeconds / tauSeconds));
}

/** Wraps into (-pi, pi] so the yaw a whole run's worth of mouse travel produces stays a sane number to send and to log. Mirrors the server's own `normalizeAngle`. */
export function normalizeAngle(radians: number): number {
  const wrapped = radians % (Math.PI * 2);
  if (wrapped > Math.PI) return wrapped - Math.PI * 2;
  if (wrapped <= -Math.PI) return wrapped + Math.PI * 2;
  return wrapped;
}

/**
 * design/gdd/camera-and-controls.md — the over-the-shoulder rig, turned by the
 * mouse.
 *
 * **The fifth amendment (2026-08-25) inverted this class.** Every version
 * before it derived the view from the character: `renderYaw` chased the
 * server's `facing`, `facing` was itself derived from the movement direction
 * the camera had just produced, and four separate amendments went into keeping
 * that loop from spinning, snapping or jittering — a frozen steering yaw, a
 * split render yaw, a re-anchor on every discrete input change, and a slew-rate
 * clamp on the realign. All of it is gone, because none of it has anything left
 * to fix: yaw and pitch are now *inputs*, accumulated straight from mouse
 * deltas, and nothing computes them from anything the camera itself produced.
 *
 * That deletion is the real substance of the change. A/D are honest strafes
 * again rather than "how you turn", `facing` follows the mouse (the server's
 * own `resolveFacing`), and the two follow from the same fact: the loop that
 * needed taming does not exist when the player is holding the wheel.
 *
 * Turning requires the pointer to be captured — mouse deltas are only
 * meaningful under Pointer Lock, where the cursor has no position to run out
 * of. `requestLook()` is called from the canvas's own `pointerdown`, so a click
 * on the world captures; Escape (or `releaseLook()`, for a UI panel that needs
 * a cursor) hands it back. `pointerLocked` is the flag callers gate gameplay
 * clicks on, so the click that captures the pointer is not also a swing.
 */
export class OverShoulderCameraController {
  private yaw: number;
  private pitch: number;
  private locked = false;
  /**
   * Whether the browser has refused to give us the pointer (2026-09-05).
   *
   * Set on `pointerlockerror`, cleared the moment a lock succeeds. Callers
   * read it through `lookUnavailable` to decide whether an unlocked click is
   * the one that captures — or whether capture is simply not on offer, in
   * which case swallowing clicks forever would disable the mouse as an input.
   */
  private lockRefused = false;
  private shoulderSideTarget: 1 | -1 = 1;
  private shoulderBlend = 1;
  private sensitivity = 1;
  private invertLook = false;

  private readonly onPointerDown = (): void => { this.requestLook(); };

  private readonly onMouseMove = (e: MouseEvent): void => {
    if (!this.locked) return;
    this.applyLookDelta(e.movementX, e.movementY);
  };

  /** Middle-mouse toggles which side the boom sits on — "corner peeking is a real tactic." */
  private readonly onMouseDown = (e: MouseEvent): void => {
    if (e.button !== 1) return;
    e.preventDefault();
    this.shoulderSideTarget = this.shoulderSideTarget === 1 ? -1 : 1;
  };

  private readonly onPointerLockChange = (): void => {
    this.locked = document.pointerLockElement === this.canvas;
    // A success clears the refusal: the browser's cooldown has passed and the
    // capture click is meaningful again.
    if (this.locked) this.lockRefused = false;
    this.onLockChange?.(this.locked);
  };

  /**
   * Pointer Lock can be refused — most often because the browser is enforcing
   * its own cooldown after an Escape, which is a security feature and not
   * something to retry around. Swallowed rather than surfaced: the player still
   * has every key, and the "click to look" prompt is already on screen telling
   * them what to do about it.
   */
  private readonly onPointerLockError = (): void => {
    this.locked = false;
    this.lockRefused = true;
    this.onLockChange?.(false);
  };

  private readonly onContextMenu = (e: MouseEvent): void => e.preventDefault();
  private readonly onAuxClick = (e: MouseEvent): void => { if (e.button === 1) e.preventDefault(); };

  /** Fired whenever the pointer is captured or handed back, so the UI can show/hide its prompt. */
  onLockChange: ((locked: boolean) => void) | null = null;

  constructor(private readonly canvas: HTMLCanvasElement, initialYaw = 0, basePitch = DEFAULT_BASE_PITCH) {
    this.yaw = normalizeAngle(initialYaw);
    this.pitch = Math.min(MAX_PITCH, Math.max(MIN_PITCH, basePitch));
  }

  get viewYaw(): number { return this.yaw; }
  get viewPitch(): number { return this.pitch; }
  /** Continuous -1..1 during the shoulder-swap blend, not just the discrete ±1 target. */
  get shoulderSide(): number { return this.shoulderBlend; }
  /** Whether the mouse is currently captured — callers gate world clicks on this so the capturing click isn't also a swing. */
  get pointerLocked(): boolean { return this.locked; }

  /**
   * Whether capturing the pointer is off the table entirely (2026-09-05).
   *
   * User: "I would also like the attack to be triggered with the left mouse
   * button instead of J." It already was — `LMB attack` is in the legend and
   * in the tutorial — but the swing was gated on `pointerLocked`, so when the
   * browser refused the lock the mouse stopped being a weapon and the only
   * way left to attack was J/K/L. Which is exactly what the player found.
   *
   * Pointer Lock **is** refusable, and this file already said so: Chrome
   * enforces a cooldown after an Escape and can decline outright. Some embeds
   * do not implement it at all. A gate on a capability the browser can revoke
   * is a gate that silently disarms the primary attack button.
   *
   * True means "do not wait for a capture that is not coming" — callers treat
   * an unlocked click as a real click. Mouse-look degrades; combat does not.
   */
  get lookUnavailable(): boolean {
    return this.lockRefused
      || (typeof Element !== 'undefined' && !('requestPointerLock' in Element.prototype));
  }

  /**
   * Turns the view by one mouse movement, in raw device pixels.
   *
   * Right/down are positive, matching `MouseEvent.movementX/Y`. Moving the
   * mouse right *decreases* yaw, because forward is `(sin yaw, cos yaw)` and
   * the view's right is `(-cos yaw, sin yaw)` — turning toward that vector
   * takes yaw negative. Public for its own tests: jsdom has no Pointer Lock, so
   * this is where the input maths is verified without a browser.
   */
  applyLookDelta(dxPx: number, dyPx: number): void {
    const pitchDir = this.invertLook ? -1 : 1;
    this.yaw = normalizeAngle(this.yaw - dxPx * MOUSE_YAW_SENSITIVITY * this.sensitivity);
    this.pitch = Math.min(
      MAX_PITCH,
      Math.max(MIN_PITCH, this.pitch - dyPx * MOUSE_PITCH_SENSITIVITY * this.sensitivity * pitchDir),
    );
  }

  /**
   * The player's look preferences, from the settings screen.
   *
   * A multiplier on both axes rather than two separate numbers: the yaw/pitch
   * asymmetry above is a design decision about the pitch clamp, not a taste, so
   * a player scaling their sensitivity should keep it rather than flatten it.
   */
  setLookPreferences(prefs: { sensitivity?: number; invertLook?: boolean }): void {
    if (typeof prefs.sensitivity === 'number' && Number.isFinite(prefs.sensitivity)) {
      this.sensitivity = Math.min(3, Math.max(0.25, prefs.sensitivity));
    }
    if (typeof prefs.invertLook === 'boolean') this.invertLook = prefs.invertLook;
  }

  /**
   * Points the view along `yaw` outright.
   *
   * Called once, when the local player's spawn facing first arrives. Without
   * it every delver would start a run looking due north: the camera no longer
   * derives its yaw from `facing`, so a spawn orientation that nothing reads
   * is a spawn orientation that does not exist — and since aim now *drives*
   * `facing`, the first aim message would overwrite the spawn facing with
   * whatever the camera happened to hold.
   */
  alignTo(yaw: number): void {
    this.yaw = normalizeAngle(yaw);
  }

  /** Asks for the pointer. Safe to call when already captured, or in an environment with no Pointer Lock at all. */
  requestLook(): void {
    if (this.locked) return;
    this.canvas.requestPointerLock?.();
  }

  /** Hands the pointer back — for a panel that needs a cursor (the emote picker, the run-end screen). */
  releaseLook(): void {
    if (document.pointerLockElement === this.canvas) document.exitPointerLock();
  }

  /**
   * Per-frame smoothing. Only the shoulder-swap blend remains — yaw and pitch
   * are inputs now and are applied the moment the mouse moves, which is the
   * whole point: a filter between the hand and the view is latency.
   */
  update(dtSeconds: number): void {
    this.shoulderBlend = expApproach(this.shoulderBlend, this.shoulderSideTarget, dtSeconds, SHOULDER_BLEND_TAU_S);
  }

  /**
   * Rotates a camera-local input vector (x = strafe right, z = forward) into
   * the world-space direction the server expects. `right` is `forward × up`
   * (three.js's own lookAt convention), which at yaw=0 (forward=+Z) works out
   * to -X, not +X — the same vector `computeShoulderCameraPose` offsets the
   * shoulder along, so "your right shoulder" and "strafe right" agree.
   */
  worldDirection(local: { x: number; z: number }): { x: number; z: number } {
    const sinYaw = Math.sin(this.yaw);
    const cosYaw = Math.cos(this.yaw);
    return {
      x: local.z * sinYaw - local.x * cosYaw,
      z: local.z * cosYaw + local.x * sinYaw,
    };
  }

  start(): void {
    this.canvas.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('mousemove', this.onMouseMove);
    this.canvas.addEventListener('mousedown', this.onMouseDown);
    this.canvas.addEventListener('contextmenu', this.onContextMenu);
    this.canvas.addEventListener('auxclick', this.onAuxClick);
    document.addEventListener('pointerlockchange', this.onPointerLockChange);
    document.addEventListener('pointerlockerror', this.onPointerLockError);
  }

  stop(): void {
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('mousemove', this.onMouseMove);
    this.canvas.removeEventListener('mousedown', this.onMouseDown);
    this.canvas.removeEventListener('contextmenu', this.onContextMenu);
    this.canvas.removeEventListener('auxclick', this.onAuxClick);
    document.removeEventListener('pointerlockchange', this.onPointerLockChange);
    document.removeEventListener('pointerlockerror', this.onPointerLockError);
    this.releaseLook();
  }
}
